import { useEffect, useMemo } from 'react';
import { CheckboxGroup, CheckboxOption } from './CheckboxDropdown';

type SelectionMode = 'multiple' | 'single';

export function useGroupedSelection({
  groups,
  requireSelection,
  selectionMode = 'multiple',
  onChange,
  onSelectionChange,
}: {
  groups: CheckboxGroup[];
  requireSelection?: boolean;
  selectionMode?: SelectionMode;
  onChange?: (id: string, checked: boolean) => void;
  onSelectionChange?: (groups: Record<string, CheckboxOption[]>) => void;
}) {
  // Derive options and groupMap from props directly without state
  const options = useMemo(() => {
    const opts: Record<string, CheckboxOption> = {};
    groups.forEach((group) => {
      group.options.forEach((option) => {
        opts[option.id] = { ...option };
      });
    });
    return opts;
  }, [groups]);

  const groupMap = useMemo(() => {
    const gMap: Record<string, string> = {};
    groups.forEach((group, groupIndex) => {
      group.options.forEach((option) => {
        gMap[option.id] = groupIndex.toString();
      });
    });
    return gMap;
  }, [groups]);

  const wouldViolateRequirement = (optionId: string, newCheckedState: boolean): boolean => {
    if (newCheckedState) return false;

    const groupIndex = groupMap[optionId];
    if (groupIndex === undefined) return false;

    const group = groups[Number.parseInt(groupIndex)];
    const isGroupRestricted = group.requireSelection || requireSelection;

    if (!isGroupRestricted) return false;

    let checkedCount = 0;
    group.options.forEach((option) => {
      if (option.id !== optionId && options[option.id]?.checked) {
        checkedCount++;
      }
    });

    return checkedCount === 0;
  };

  useEffect(() => {
    if (selectionMode === 'single') {
      // Find first checked option, or default to first
      const firstCheckedId = Object.values(options).find((o) => o.checked)?.id || groups[0].options[0].id;

      handleCheckedChange(firstCheckedId, true);
    }
  }, [groups]);

  const handleCheckedChange = (optionId: string, checked: boolean) => {
    const groupIndex = Number(groupMap[optionId]);
    const group = groups[groupIndex];
    if (!group) return;

    // Tabs behavior: ignore uncheck
    if (selectionMode === 'single' && !checked) {
      return;
    }

    // Enforce single-select
    if (selectionMode === 'single' && checked) {
      group.options.forEach((opt) => {
        onChange?.(opt.id, opt.id === optionId);
      });
    } else {
      if (wouldViolateRequirement(optionId, checked)) return;
      onChange?.(optionId, checked);
    }

    if (onSelectionChange) {
      const selected: Record<string, CheckboxOption[]> = {};
      groups.forEach((g, gi) => {
        g.options.forEach((opt) => {
          const isChecked =
            selectionMode === 'single' ? opt.id === optionId && checked : opt.id === optionId ? checked : opt.checked;

          if (isChecked) {
            selected[gi] ??= [];
            selected[gi].push({ ...opt, checked: true });
          }
        });
      });
      onSelectionChange(selected);
    }
  };

  return {
    options,
    handleCheckedChange,
    wouldViolateRequirement,
  };
}
